import React, { useState, useEffect } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import Cookies from "js-cookie";
import {
    Form,
    Input,
    InputNumber,
    Button,
    notification,
    Card,
    Divider,
    Tag,
} from "antd";
import {
    HiOutlineChevronLeft,
    HiOutlinePlus
} from 'react-icons/hi';

const HealthMetrics = () => {
    const history = useHistory();
    const { patientId, bookingId } = useParams();
    const location = useLocation();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [diseases, setDiseases] = useState([]);
    const [newDisease, setNewDisease] = useState("");

    // Get initial values from location state if available (for editing)
    const initialData = location.state?.appointmentData || {};

    useEffect(() => {
        if (initialData.pre_exits_disease) {
            try {
                const parsed = typeof initialData.pre_exits_disease === 'string'
                    ? JSON.parse(initialData.pre_exits_disease)
                    : initialData.pre_exits_disease;
                setDiseases(Array.isArray(parsed) ? parsed : []);
            } catch (e) {
                setDiseases([]);
            }
        }
    }, [initialData]);

    const handleAddDisease = () => {
        if (newDisease.trim() && !diseases.includes(newDisease.trim())) {
            setDiseases([...diseases, newDisease.trim()]);
            setNewDisease("");
        }
    };

    const handleRemoveDisease = (removedTag) => {
        const newTags = diseases.filter((tag) => tag !== removedTag);
        setDiseases(newTags);
    };

    const onFinish = async (values) => {
        setLoading(true);
        try {
            let finalDiseases = [...diseases];
            if (newDisease.trim() && !finalDiseases.includes(newDisease.trim())) {
                finalDiseases.push(newDisease.trim());
            }

            const payload = {
                patientid: patientId,
                booking_id: bookingId,
                height: values.height != null ? String(values.height) : "",
                weight: values.weight != null ? String(values.weight) : "",
                blood_pressure: values.blood_pressure != null ? String(values.blood_pressure) : "",
                heartrate: values.heartrate != null ? String(values.heartrate) : "",
                body_temperature: values.body_temperature != null ? String(values.body_temperature) : "",
                pre_exist_disease: finalDiseases.length > 0 ? JSON.stringify(finalDiseases) : "",
            };

            const response = await axiosInstance.post("doctor/insert_patient_walkin_health_matrics", payload);

            if (response.data.status === 1) {
                notification.success({
                    message: initialData.health_matrics_status === 1 ? "Health Metrics Updated" : "Health Metrics Added",
                    description: "Patient health statistics have been successfully saved."
                });
                history.goBack();
            } else {
                notification.error({ message: response.data.msg || "Failed to save health metrics" });
            }
        } catch (err) {
            console.error("Health metrics save error", err);
            notification.error({ message: "An error occurred while saving" });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in duration-500 p-6">
            <div className="flex items-center justify-between bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => history.goBack()}
                        className="p-2 hover:bg-teal-50 rounded-xl text-teal-600 transition-colors"
                    >
                        <HiOutlineChevronLeft size={24} />
                    </button>
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800 tracking-tight">Patient Health Metrics</h2>
                        <p className="text-gray-500 text-sm mt-1">Record vital signs and clinical measurements</p>
                    </div>
                </div>
                <div className="bg-teal-50 px-4 py-2 rounded-xl border border-teal-100">
                    <span className="text-teal-700 font-bold text-xs uppercase tracking-widest">Booking ID: #{bookingId}</span>
                </div>
            </div>

            <Card className="rounded-3xl border-none shadow-sm overflow-hidden p-2">
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    initialValues={{
                        height: initialData.height,
                        weight: initialData.weight,
                        blood_pressure: initialData.blood_pressure,
                        heartrate: initialData.heart_rate,
                        body_temperature: initialData.body_temperature,
                    }}
                    className="space-y-6"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                        <Form.Item
                            label={<span className="font-bold text-gray-700">Height (cm)</span>}
                            name="height"
                            rules={[{ required: true, message: 'Required' }]}
                        >
                            <InputNumber placeholder="170" className="w-full h-12 rounded-xl flex items-center" />
                        </Form.Item>

                        <Form.Item
                            label={<span className="font-bold text-gray-700">Weight (kg)</span>}
                            name="weight"
                            rules={[{ required: true, message: 'Required' }]}
                        >
                            <InputNumber placeholder="70" className="w-full h-12 rounded-xl flex items-center" />
                        </Form.Item>

                        <Form.Item
                            label={<span className="font-bold text-gray-700">Blood Pressure (mmHg)</span>}
                            name="blood_pressure"
                            rules={[{ required: true, message: 'Required' }]}
                        >
                            <Input placeholder="120/80" className="h-12 rounded-xl" />
                        </Form.Item>

                        <Form.Item
                            label={<span className="font-bold text-gray-700">Heart Rate (bpm)</span>}
                            name="heartrate"
                            rules={[{ required: true, message: 'Required' }]}
                        >
                            <InputNumber placeholder="72" className="w-full h-12 rounded-xl flex items-center" />
                        </Form.Item>

                        <Form.Item
                            label={<span className="font-bold text-gray-700">Body Temperature (°C or °F)</span>}
                            name="body_temperature"
                            rules={[{ required: true, message: 'Required' }]}
                        >
                            <InputNumber placeholder="36.6" className="w-full h-12 rounded-xl flex items-center" />
                        </Form.Item>

                        <div className="col-span-1 md:col-span-2 space-y-2">
                            <label className="font-bold text-gray-700 block mb-1">Pre-existing Diseases</label>
                            <div className="flex gap-2">
                                <Input
                                    placeholder="Enter disease and click add"
                                    value={newDisease}
                                    onChange={(e) => setNewDisease(e.target.value)}
                                    onPressEnter={handleAddDisease}
                                    className="h-12 rounded-xl"
                                />
                                <Button
                                    onClick={handleAddDisease}
                                    className="h-12 w-12 flex items-center justify-center rounded-xl bg-teal-50 text-teal-600 border-teal-100"
                                >
                                    <HiOutlinePlus size={20} />
                                </Button>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-3 min-h-[40px] p-4 bg-gray-50 rounded-2xl border border-dashed border-gray-200" style={{ minHeight: '80px' }}>
                                {diseases.length > 0 ? (
                                    diseases.map((tag) => (
                                        <Tag
                                            key={tag}
                                            closable
                                            onClose={() => handleRemoveDisease(tag)}
                                            className="bg-white px-3 py-1 rounded-lg border-gray-200 text-gray-600 font-semibold flex items-center gap-1"
                                        >
                                            {tag}
                                        </Tag>
                                    ))
                                ) : (
                                    <span className="text-gray-400 text-xs italic">No pre-existing diseases added</span>
                                )}
                            </div>
                        </div>
                    </div>

                    <Divider className="my-8" />

                    <div className="flex justify-end gap-4 pb-4">
                        <Button
                            size="large"
                            className="rounded-xl px-10 h-12 font-bold border-gray-200 text-gray-500"
                            onClick={() => history.goBack()}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            size="large"
                            loading={loading}
                            className="bg-teal-600 hover:bg-teal-700 rounded-xl px-14 h-12 font-bold shadow-lg shadow-teal-600/20"
                        >
                            {initialData.health_matrics_status === 1 ? "Update Metrics" : "Save Metrics"}
                        </Button>
                    </div>
                </Form>
            </Card>
        </div>
    );
};

export default HealthMetrics;
